import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { Usuario, AuthContextType } from '../types';
import UsuariosServiceAPI from '../services/usuariosServiceAPI';
import SessoesService from '../services/sessoesService';
import LogsUsuariosService from '../services/logsUsuariosService';
import { PerfisService } from '../services/perfisService';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<Usuario | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    localStorage.removeItem('user');
    localStorage.removeItem('@sistema-up:token');
    localStorage.removeItem('@sistema-up:refreshToken');
    localStorage.removeItem('@sistema-up:usuario');
    localStorage.removeItem('lastActivity');
    setUser(null);
    setLoading(false);
  }, []);

  const login = async (login: string, senha: string, captcha?: string) => {
    try {
      setLoading(true);
      const { user: authUser, error, requiresCaptcha } = await UsuariosServiceAPI.login(login, senha, captcha);
      
      if (error) {
        const customError: any = new Error(error);
        customError.requiresCaptcha = requiresCaptcha;
        throw customError;
      }
      
      if (authUser) {
        PerfisService.limparCache();
        // O login já retorna os dados necessários, não precisa buscarPorId
        const usuarioFormatado: Usuario = {
          id: authUser.id,
          email: authUser.email || '',
          nome: authUser.nome,
          perfil: (authUser.perfil as any) || 'admin', // Usar perfil_id do backend
          filiais: authUser.filiais || [],
          ativo: authUser.ativo ?? true,
          created_at: authUser.created_at || new Date().toISOString(),
          updated_at: authUser.updated_at || new Date().toISOString(),
          primeiro_acesso: authUser.primeiro_acesso || false,
          foto_perfil: authUser.foto_perfil,
          perfil_id: (authUser.perfil as any) // Adicionar perfil_id para uso no sistema de permissões
        };
        setUser(usuarioFormatado);
        // Salvar no localStorage
        localStorage.setItem('user', JSON.stringify(usuarioFormatado));
        
        // Registrar sessão ativa (temporariamente desativado para teste)
        // await SessoesService.registrarSessao(usuarioFormatado);
        
        // Registrar log de login (temporariamente desativado para teste)
        // await LogsUsuariosService.registrarLogin(usuarioFormatado);
        
        return usuarioFormatado;
      }
      
      return null;
    } catch (error: any) {
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const register = async (data: { nome: string; email: string; senha: string; confirmarSenha: string }) => {
    try {
      setLoading(true);
      
      if (data.senha !== data.confirmarSenha) {
        throw new Error('As senhas não coincidem');
      }

      // Verificar se email já existe
      const { existe, error: checkError } = await UsuariosServiceAPI.verificarEmailExistente(data.email);
      if (checkError) {
        throw checkError;
      }
      if (existe) {
        throw new Error('Este email já está cadastrado no sistema');
      }

      const authData = await UsuariosServiceAPI.criar({
        login: data.email,
        nome: data.nome,
        email: data.email,
        senha: data.senha,
        perfil_id: 'fornecedor',
        primeiro_acesso: true,
        filiais_permitidas: ['FILIAL_1'],
        ativo: true
      });
      
      if (!authData) {
        throw new Error('Erro ao criar usuário');
      }
      
      // Fazer login automático após cadastro
      await login(data.email, data.senha);
    } catch (error: any) {
      setLoading(false);
      throw error;
    }
  };

  const logout = async () => {
    try {
      setLoading(true);
      
      // Remover sessão ativa do usuário
      if (user) {
        await SessoesService.removerSessao(user.id);
        
        // Registrar log de logout
        await LogsUsuariosService.registrarLogout(user);
      }
      
      setUser(null);
      localStorage.removeItem('user');
      PerfisService.limparCache();
    } catch (error: any) {
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const updateProfile = async (updates: Partial<{ nome: string; filiais: string[] }>) => {
    try {
      if (!user) {
        throw new Error('Usuário não encontrado');
      }

      // Simulação de atualização - implementar com Supabase real
      setUser(prev => prev ? { ...prev, ...updates } : null);
    } catch (error: any) {
      throw error;
    }
  };

  const temPermissao = (permissao: string): boolean => {
    if (!user) return false;
    
    // Mapeamento de permissões por perfil
    const permissoesPorPerfil: Record<string, string[]> = {
      'admin': ['solicitarAgendamento', 'fecharDia', 'gerenciarUsuarios', 'gerenciarFiliais', 'verRelatorios', 'gerenciarCargas', 'editarCarga', 'excluirCarga', 'alterarStatus', 'gerenciarOcorrencias', 'validarDocumentos'],
      'gerente': ['solicitarAgendamento', 'fecharDia', 'gerenciarCargas', 'editarCarga', 'alterarStatus', 'gerenciarOcorrencias', 'validarDocumentos'],
      'operador': ['gerenciarCargas', 'editarCarga', 'alterarStatus', 'gerenciarOcorrencias'],
      'fornecedor': ['solicitarAgendamento', 'validarDocumentos']
    };
    
    const perfil = user.perfil;
    const permissoes = permissoesPorPerfil[perfil] || [];
    
    return permissoes.includes(permissao);
  };

  const value: AuthContextType = {
    user,
    loading,
    login,
    register,
    logout,
    updateProfile,
    temPermissao
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
